import type {PickerItem} from "../pickerItem";

export type PickerEntry<I extends PickerItem = PickerItem> =
    | {kind: "heading"; key: string; label: string}
    | {kind: "row"; key: string; item: I; row: number};

export interface PickerEntries<I extends PickerItem = PickerItem> {
    /** Headings and rows in display order: what the virtualizer counts. */
    entries: PickerEntry<I>[];
    /** The rows alone, in display order: what the keyboard moves through. */
    rows: I[];
    /** For each row, its index in `entries`. */
    rowEntryIndex: number[];
}

/*
    Prefixed, so an item whose key happens to read like a heading's can never collide with it in
    the virtualizer's size cache.
 */
const headingKey = (groupKey: string) => `heading:${groupKey}`;
const rowKey = (key: string) => `row:${key}`;

/**
 * Items → heading and row entries.
 *
 * Groups open in the order their first item appears, and every later item of a group joins it,
 * consecutive or not, so a host that interleaves scopes still gets one heading per scope. Items
 * without a `groupKey` stay together where the first of them appeared, under no heading.
 */
export function pickerEntries<I extends PickerItem>(items: readonly I[]): PickerEntries<I> {
    const groups = new Map<string | undefined, I[]>();

    items.forEach(item => {
        const group = groups.get(item.groupKey);

        if (group) group.push(item);
        else groups.set(item.groupKey, [item]);
    });

    const entries: PickerEntry<I>[] = [];
    const rows: I[] = [];
    const rowEntryIndex: number[] = [];

    groups.forEach((members, groupKey) => {
        if (groupKey !== undefined) {
            entries.push({
                kind: "heading",
                key: headingKey(groupKey),
                label: members[0].groupLabel ?? groupKey,
            });
        }

        members.forEach(item => {
            rowEntryIndex.push(entries.length);
            entries.push({kind: "row", key: rowKey(item.key), item, row: rows.length});
            rows.push(item);
        });
    });

    return {entries, rows, rowEntryIndex};
}

export default pickerEntries;
